import { useGeneralGetHook } from "api/hooks/allHooks";
import { AllCacheKeys, createConfigTabCacheKeys } from "features/shared/lib/technical-config-tab/api/cacheKeys";
import { createConfigCrud } from "features/shared/lib/technical-config-tab/api/crudCallers";
import { buildOptimisticGroupMutationOptions } from "features/shared/lib/technical-config-tab/api/mutations";
import { useConfigDetailQuery, useConfigGroupsQuery } from "features/shared/lib/technical-config-tab/api/queryHooks";
import { useActiveScenarioName } from "features/technical-data/scenario/hooks/useActiveScenarioName";
import { useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";

import type {
  CommonProps,
  ConstructionGroup,
  LayeredConstructionApiCatalogItem,
  LayeredConstructionGroupDetail,
  LayeredConstructionSavePayload,
  SaveConstructionConfigProps,
} from "../model/types";
import type { LayeredConstructionVariant } from "../model/variants";

import { normalizeCatalogItem } from "../lib/catalogMappers";

export const createLayeredConstructionApi = (variant: LayeredConstructionVariant) => {
  const cacheKeys: AllCacheKeys = {
    ...createConfigTabCacheKeys(variant.cacheRoot),
    catalog: [variant.cacheRoot, "catalog"] as const,
  };

  const crud = createConfigCrud<LayeredConstructionSavePayload>(variant.groupDetailPath);

  const saveConfig = ({ data, hasTechnology, projectId, scenario, userDefinedType }: SaveConstructionConfigProps) =>
    crud.save({ data, isExisting: hasTechnology, projectId, recordId: userDefinedType, scenario });

  const deleteConfig = ({ projectId, scenario, userDefinedType }: CommonProps) =>
    crud.remove({ projectId, recordId: userDefinedType, scenario });

  const useGetGroups = (projectId?: string) => {
    const scenario = useActiveScenarioName();
    return useConfigGroupsQuery<ConstructionGroup>(
      cacheKeys.groups(projectId, scenario),
      variant.groupsPath(projectId, scenario),
      Boolean(projectId && scenario)
    );
  };

  const useGetDetail = (projectId?: string, userDefinedType?: string) => {
    const scenario = useActiveScenarioName();
    const enabled = Boolean(projectId && userDefinedType && scenario);
    const url = enabled ? variant.groupDetailPath(projectId as string, userDefinedType as string, scenario) : undefined;
    return useConfigDetailQuery<LayeredConstructionGroupDetail>(
      cacheKeys.detail(projectId, userDefinedType, scenario),
      url,
      enabled
    );
  };

  const useGetCatalog = (enabled = true) => {
    const query = useGeneralGetHook<LayeredConstructionApiCatalogItem[]>(cacheKeys.catalog, variant.catalogPath(), {
      enabled,
    });
    const data = useMemo(() => query.data?.map((item) => normalizeCatalogItem(item, variant)) ?? [], [query.data]);
    return { ...query, data };
  };

  const useSaveConfig = () => {
    const scenario = useActiveScenarioName();
    const queryClient = useQueryClient();
    return useMutation(
      (variables: SaveConstructionConfigProps) => saveConfig({ ...variables, scenario }),
      buildOptimisticGroupMutationOptions<ConstructionGroup, SaveConstructionConfigProps>({
        applyOptimisticPatch: (group) => ({ ...group, hasTechnology: true }),
        cacheKeys,
        getProjectId: (variables) => variables.projectId,
        getRecordId: (variables) => variables.userDefinedType,
        getScenario: () => scenario,
        matchesGroup: (group, variables) => group.userDefinedType === variables.userDefinedType,
        queryClient,
        successMessageKey: `TechnicalData.${variant.i18nKey}.saveSuccess`,
      })
    );
  };

  const useDeleteConfig = () => {
    const scenario = useActiveScenarioName();
    const queryClient = useQueryClient();
    return useMutation(
      (variables: CommonProps) => deleteConfig({ ...variables, scenario }),
      buildOptimisticGroupMutationOptions<ConstructionGroup, CommonProps>({
        applyOptimisticPatch: (group) => ({ ...group, hasTechnology: false }),
        cacheKeys,
        getProjectId: (variables) => variables.projectId,
        getRecordId: (variables) => variables.userDefinedType,
        getScenario: () => scenario,
        matchesGroup: (group, variables) => group.userDefinedType === variables.userDefinedType,
        queryClient,
        successMessageKey: `TechnicalData.${variant.i18nKey}.deleteSuccess`,
      })
    );
  };

  return {
    cacheKeys,
    deleteConfig,
    saveConfig,
    useDeleteConfig,
    useGetCatalog,
    useGetDetail,
    useGetGroups,
    useSaveConfig,
  };
};

export type LayeredConstructionApi = ReturnType<typeof createLayeredConstructionApi>;
